/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  NonAcceptanceType,
  ReceivingFacilityRequestStatus,
} from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ReceivingFacilityRequestProps {
  id: string;
  status: ReceivingFacilityRequestStatus;
  cardId: string;
  requestId: string;
  medicalFacilityId: string;
  nonAcceptanceType: NonAcceptanceType | null;
  nonAcceptanceReasonId: string | null;
  referredToFacilityId: string | null;
  requestLocationId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ReceivingFacilityRequestRecord extends Record<ReceivingFacilityRequestProps>({
  id: null as any,
  status: null as any,
  cardId: null as any,
  requestId: null as any,
  medicalFacilityId: null as any,
  nonAcceptanceType: null as any,
  nonAcceptanceReasonId: null as any,
  referredToFacilityId: null as any,
  requestLocationId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    status: ReceivingFacilityRequestStatus;
    cardId: string;
    requestId: string;
    medicalFacilityId: string;
    nonAcceptanceType: NonAcceptanceType | null;
    nonAcceptanceReasonId: string | null;
    referredToFacilityId: string | null;
    requestLocationId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ReceivingFacilityRequestRecord {
    if (json == null) {
      throw new Error('ReceivingFacilityRequestRecord.fromJS: parameter cannot be null');
    }
    return new ReceivingFacilityRequestRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ReceivingFacilityRequestJson.id',
      ),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', ReceivingFacilityRequestStatus.Pending),
        'ReceivingFacilityRequestJson.status',
      ),
      cardId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'cardId', '00000000-0000-0000-0000-000000000000'),
        'ReceivingFacilityRequestJson.cardId',
      ),
      requestId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'requestId', '00000000-0000-0000-0000-000000000000'),
        'ReceivingFacilityRequestJson.requestId',
      ),
      medicalFacilityId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'medicalFacilityId', '00000000-0000-0000-0000-000000000000'),
        'ReceivingFacilityRequestJson.medicalFacilityId',
      ),
      nonAcceptanceType: json.nonAcceptanceType,
      nonAcceptanceReasonId: json.nonAcceptanceReasonId,
      referredToFacilityId: json.referredToFacilityId,
      requestLocationId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'requestLocationId', '00000000-0000-0000-0000-000000000000'),
        'ReceivingFacilityRequestJson.requestLocationId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'ReceivingFacilityRequestJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    status: ReceivingFacilityRequestStatus;
    cardId: string;
    requestId: string;
    medicalFacilityId: string;
    nonAcceptanceType: NonAcceptanceType | null;
    nonAcceptanceReasonId: string | null;
    referredToFacilityId: string | null;
    requestLocationId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ReceivingFacilityRequestRecord, Error[]] {
    if (json == null) {
      throw new Error('ReceivingFacilityRequestRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ReceivingFacilityRequestRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ReceivingFacilityRequestJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', ReceivingFacilityRequestStatus.Pending),
            'ReceivingFacilityRequestJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cardId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'cardId', '00000000-0000-0000-0000-000000000000'),
            'ReceivingFacilityRequestJson.cardId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'requestId', '00000000-0000-0000-0000-000000000000'),
            'ReceivingFacilityRequestJson.requestId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      medicalFacilityId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'medicalFacilityId', '00000000-0000-0000-0000-000000000000'),
            'ReceivingFacilityRequestJson.medicalFacilityId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      nonAcceptanceType: tryOrDefault(
        () => json.nonAcceptanceType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      nonAcceptanceReasonId: tryOrDefault(
        () => json.nonAcceptanceReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      referredToFacilityId: tryOrDefault(
        () => json.referredToFacilityId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestLocationId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'requestLocationId', '00000000-0000-0000-0000-000000000000'),
            'ReceivingFacilityRequestJson.requestLocationId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'ReceivingFacilityRequestJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ReceivingFacilityRequestProps) {
    super(props);
    if (props == null) {
      throw new Error('ReceivingFacilityRequestRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ReceivingFacilityRequestViewModelBase
  implements ReceivingFacilityRequestProps
{
  protected readonly _props: ReceivingFacilityRequestProps;
  private _json?: ReceivingFacilityRequestProps;

  get id(): string {
    return this._props.id;
  }
  get status(): ReceivingFacilityRequestStatus {
    return this._props.status;
  }
  get cardId(): string {
    return this._props.cardId;
  }
  get requestId(): string {
    return this._props.requestId;
  }
  get medicalFacilityId(): string {
    return this._props.medicalFacilityId;
  }
  get nonAcceptanceType(): NonAcceptanceType | null {
    return this._props.nonAcceptanceType;
  }
  get nonAcceptanceReasonId(): string | null {
    return this._props.nonAcceptanceReasonId;
  }
  get referredToFacilityId(): string | null {
    return this._props.referredToFacilityId;
  }
  get requestLocationId(): string {
    return this._props.requestLocationId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAccepted() {
    return this._props.status === ReceivingFacilityRequestStatus.Accepted;
  }
  public get isStatusCancelled() {
    return this._props.status === ReceivingFacilityRequestStatus.Cancelled;
  }
  public get isNonAcceptanceTypeCancelled() {
    return this._props.nonAcceptanceType === NonAcceptanceType.Cancelled;
  }
  public get isStatusDenied() {
    return this._props.status === ReceivingFacilityRequestStatus.Denied;
  }
  public get isNonAcceptanceTypeDenied() {
    return this._props.nonAcceptanceType === NonAcceptanceType.Denied;
  }
  public get isStatusDiverted() {
    return this._props.status === ReceivingFacilityRequestStatus.Diverted;
  }
  public get isNonAcceptanceTypeDiverted() {
    return this._props.nonAcceptanceType === NonAcceptanceType.Diverted;
  }
  public get isPending() {
    return this._props.status === ReceivingFacilityRequestStatus.Pending;
  }
  public get isReferred() {
    return this._props.nonAcceptanceType === NonAcceptanceType.Referred;
  }

  public isAnyStatus(...receivingFacilityRequestStatus: ReceivingFacilityRequestStatus[]) {
    return receivingFacilityRequestStatus.includes(this._props.status);
  }
  public isAnyNonAcceptanceType(...nonAcceptanceType: NonAcceptanceType[]) {
    return (
      this._props.nonAcceptanceType != null &&
      nonAcceptanceType.includes(this._props.nonAcceptanceType)
    );
  }

  constructor(props: ReceivingFacilityRequestProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        status: this._props.status,
        cardId: this._props.cardId,
        requestId: this._props.requestId,
        medicalFacilityId: this._props.medicalFacilityId,
        nonAcceptanceType: this._props.nonAcceptanceType,
        nonAcceptanceReasonId: this._props.nonAcceptanceReasonId,
        referredToFacilityId: this._props.referredToFacilityId,
        requestLocationId: this._props.requestLocationId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllReceivingFacilityRequestEntities = (state: AppState) =>
  state.entityStores.receivingFacilityRequest.entities;

export const selectReceivingFacilityRequestById = (id: string) => (state: AppState) =>
  state.entityStores.receivingFacilityRequest.entities.get(id);

export const selectReceivingFacilityRequestMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.receivingFacilityRequest.meta.get(id);

export const selectReceivingFacilityRequestAndMetadataById = (id: string) =>
  createSelector(
    selectReceivingFacilityRequestById(id),
    selectReceivingFacilityRequestMetadataById(id),
    (receivingFacilityRequest, meta) => ({ receivingFacilityRequest, meta }),
  );

export function selectReceivingFacilityRequestEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllReceivingFacilityRequestEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ReceivingFacilityRequestRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectReceivingFacilityRequestEntitiesByStatus(
  status: ReceivingFacilityRequestStatus,
) {
  return createSelector(selectAllReceivingFacilityRequestEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectReceivingFacilityRequestEntitiesByCardId(cardId: string) {
  return createSelector(selectAllReceivingFacilityRequestEntities, (entities) =>
    entities.filter((e) => e.cardId === cardId).valueSeq(),
  );
}

export function selectReceivingFacilityRequestEntitiesByRequestId(requestId: string) {
  return createSelector(selectAllReceivingFacilityRequestEntities, (entities) =>
    entities.filter((e) => e.requestId === requestId).valueSeq(),
  );
}

export function selectReceivingFacilityRequestEntitiesByMedicalFacilityId(
  medicalFacilityId: string,
) {
  return createSelector(selectAllReceivingFacilityRequestEntities, (entities) =>
    entities.filter((e) => e.medicalFacilityId === medicalFacilityId).valueSeq(),
  );
}

export function selectReceivingFacilityRequestEntitiesByNonAcceptanceType(
  nonAcceptanceType: NonAcceptanceType,
) {
  return createSelector(selectAllReceivingFacilityRequestEntities, (entities) =>
    entities.filter((e) => e.nonAcceptanceType === nonAcceptanceType).valueSeq(),
  );
}

export function selectReceivingFacilityRequestEntitiesByNonAcceptanceReasonId(
  nonAcceptanceReasonId: string,
) {
  return createSelector(selectAllReceivingFacilityRequestEntities, (entities) =>
    entities.filter((e) => e.nonAcceptanceReasonId === nonAcceptanceReasonId).valueSeq(),
  );
}

export function selectReceivingFacilityRequestEntitiesByReferredToFacilityId(
  referredToFacilityId: string,
) {
  return createSelector(selectAllReceivingFacilityRequestEntities, (entities) =>
    entities.filter((e) => e.referredToFacilityId === referredToFacilityId).valueSeq(),
  );
}

export function selectReceivingFacilityRequestEntitiesByRequestLocationId(
  requestLocationId: string,
) {
  return createSelector(selectAllReceivingFacilityRequestEntities, (entities) =>
    entities.filter((e) => e.requestLocationId === requestLocationId).valueSeq(),
  );
}

export function selectReceivingFacilityRequestEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllReceivingFacilityRequestEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
